import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuth, getDefaultRedirect } from '@/hooks/useAuth';
import * as authApi from '@/api/auth';
import { useAcademy } from '@/contexts/AcademyContext';
import { academyRegister } from '@/api/academy';
import { RoleLoginShell } from '@/components/auth/RoleLoginShell';
import { ROLE_LOGIN_THEMES } from '@/components/auth/roleLoginThemes';

const THEME = ROLE_LOGIN_THEMES['student_register'];

export function RegisterPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, login, applyAuth } = useAuth();
  const { slug, branding, isLoading } = useAcademy();

  const [regMode, setRegMode] = useState<'email' | 'phone'>('email');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Phone OTP state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('91');
  const [otpStep, setOtpStep] = useState<'phone' | 'otp' | 'name'>('phone');
  const [otpCode, setOtpCode] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  if (isAuthenticated && user) {
    navigate(getDefaultRedirect(user.roles), { replace: true });
    return null;
  }

  const handleAcademySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug) return;
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); setError('');
    try {
      const result = await academyRegister(slug, { display_name: displayName.trim(), email: email.trim(), password });
      localStorage.setItem('access_token', result.auth.access_token);
      localStorage.setItem('refresh_token', result.auth.refresh_token);
      if (branding?.academy_id) sessionStorage.setItem('academy_id', branding.academy_id);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); setError('');
    try {
      await authApi.register(email.trim(), password, displayName.trim());
      const response = await login(email.trim(), password);
      navigate(getDefaultRedirect(response.user.roles), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber.trim() || phoneNumber.length < 10) {
      setError('Please enter a valid mobile number');
      return;
    }
    setError(''); setLoading(true);
    try {
      const data = await authApi.requestOtp(phoneNumber, countryCode, 'register');
      if (data.otp) setDevOtp(data.otp);
      setResendTimer(60);
      setOtpStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length < 4) {
      setError('Please enter the complete OTP');
      return;
    }
    setError(''); setLoading(true);
    try {
      const data = await authApi.verifyOtp(phoneNumber, otpCode, countryCode, 'register');
      if (!data.verified) {
        throw new Error('Invalid OTP. Please try again.');
      }
      if (data.user_exists) {
        throw new Error('This mobile number is already registered. Please sign in instead.');
      }
      setOtpStep('name');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'OTP verification failed');
    } finally { setLoading(false); }
  };

  const handlePhoneRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) {
      setError('Please enter your name');
      return;
    }
    setError(''); setLoading(true);
    try {
      const auth = await authApi.phoneRegister(phoneNumber, countryCode, displayName.trim());
      const response = applyAuth(auth);
      navigate(getDefaultRedirect(response.user.roles), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setError(''); setLoading(true);
    try {
      const data = await authApi.requestOtp(phoneNumber, countryCode, 'register');
      if (data.otp) setDevOtp(data.otp);
      setResendTimer(60);
      setOtpCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend OTP');
    } finally { setLoading(false); }
  };

  if (slug) {
    if (isLoading) {
      return (
        <RoleLoginShell theme={THEME}>
          <div className="rl__form rl__form--center"><Loader2 size={32} className="rl__spin" /></div>
        </RoleLoginShell>
      );
    }
    if (!branding?.allow_self_registration) {
      return (
        <RoleLoginShell theme={THEME}>
          <div className="rl__form">
            <p className="rl__otp-note">Registration is not open for <strong>{branding?.name}</strong>.</p>
            <Link to="/login" className="rl__submit-link">Back to Login</Link>
          </div>
        </RoleLoginShell>
      );
    }
    return (
      <RoleLoginShell theme={THEME}>
        {error && <div className="rl__error" role="alert">{error}</div>}
        <form className="rl__form" onSubmit={handleAcademySubmit}>
          <div className="rl__field">
            <label className="rl__label" htmlFor="reg-name">Full Name</label>
            <input id="reg-name" className="rl__input" type="text" value={displayName}
              onChange={e => setDisplayName(e.target.value)} placeholder="Your full name" required autoFocus autoComplete="name" />
          </div>
          <div className="rl__field">
            <label className="rl__label" htmlFor="reg-email">Email</label>
            <input id="reg-email" className="rl__input" type="email" value={email}
              onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoComplete="email" />
          </div>
          <div className="rl__field-row">
            <div className="rl__field">
              <label className="rl__label" htmlFor="reg-pw">Password</label>
              <input id="reg-pw" className="rl__input" type="password" value={password}
                onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" required minLength={8} autoComplete="new-password" />
            </div>
            <div className="rl__field">
              <label className="rl__label" htmlFor="reg-confirm">Confirm Password</label>
              <input id="reg-confirm" className="rl__input" type="password" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" required autoComplete="new-password" />
            </div>
          </div>
          <div className="rl__terms">
            <input id="agree-acad" type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} />
            <label htmlFor="agree-acad">
              I agree to the{' '}<Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>{' '}and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
            </label>
          </div>
          <button type="submit" className="rl__submit"
            disabled={loading || !displayName.trim() || !email.trim() || !password || !confirmPassword || !agreedToTerms}>
            {loading ? <><Loader2 size={16} className="rl__spin" /> Creating account...</> : 'Create Account'}
          </button>
        </form>
      </RoleLoginShell>
    );
  }

  const meta = (
    <>
      <p className="rl__meta-row">Already have an account? <Link to="/login">Sign in</Link></p>
      <p className="rl__meta-row">Registering an academy? <Link to="/register/academy">Register Academy</Link></p>
    </>
  );

  const switchMode = (next: 'email' | 'phone') => {
    setRegMode(next);
    setError('');
    if (next === 'phone') {
      setOtpStep('phone');
      setOtpCode('');
      setDevOtp('');
    }
  };

  return (
    <RoleLoginShell theme={THEME} meta={meta}>
      <div className="rl__tabs" role="tablist" aria-label="Registration method">
        <button
          type="button"
          role="tab"
          className="rl__tab"
          aria-pressed={regMode === 'email'}
          onClick={() => switchMode('email')}
        >
          Email
        </button>
        <button
          type="button"
          role="tab"
          className="rl__tab"
          aria-pressed={regMode === 'phone'}
          onClick={() => switchMode('phone')}
        >
          Mobile · OTP
        </button>
      </div>

      {error && <div className="rl__error" role="alert">{error}</div>}

      {regMode === 'email' && (
        <form className="rl__form" onSubmit={handleSubmit}>
          <div className="rl__field">
            <label className="rl__label" htmlFor="reg-name">Full Name</label>
            <input id="reg-name" className="rl__input" type="text" value={displayName}
              onChange={e => setDisplayName(e.target.value)} placeholder="Your full name" required autoFocus autoComplete="name" />
          </div>
          <div className="rl__field">
            <label className="rl__label" htmlFor="reg-email">Email</label>
            <input id="reg-email" className="rl__input" type="email" value={email}
              onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoComplete="email" />
          </div>
          <div className="rl__field-row">
            <div className="rl__field">
              <label className="rl__label" htmlFor="reg-pw">Password</label>
              <div className="rl__password-wrap">
                <input id="reg-pw" className="rl__input" type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" required minLength={8} autoComplete="new-password" />
                <button type="button" className="rl__password-toggle" onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="rl__field">
              <label className="rl__label" htmlFor="reg-confirm">Confirm Password</label>
              <input id="reg-confirm" className="rl__input" type="password" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" required autoComplete="new-password" />
            </div>
          </div>
          <div className="rl__terms">
            <input id="agree-terms" type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} />
            <label htmlFor="agree-terms">
              I agree to the{' '}<Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>{' '}and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
            </label>
          </div>
          <button type="submit" className="rl__submit"
            disabled={loading || !displayName.trim() || !email.trim() || !password || !confirmPassword || !agreedToTerms}>
            {loading ? <><Loader2 size={16} className="rl__spin" /> Creating account...</> : 'Create Account'}
          </button>
        </form>
      )}

      {/* Phone OTP — number entry */}
      {regMode === 'phone' && otpStep === 'phone' && (
        <form className="rl__form" onSubmit={handleRequestOtp}>
          <p className="rl__otp-note">
            We&apos;ll text a one-time code to verify your mobile number.
          </p>
          <div className="rl__field">
            <label className="rl__label" htmlFor="reg-phone">Mobile Number</label>
            <div className="rl__phone-group">
              <select
                id="reg-country-code"
                className="rl__select"
                value={countryCode}
                onChange={e => setCountryCode(e.target.value)}
                aria-label="Country code"
              >
                <option value="91">+91</option>
                <option value="1">+1</option>
                <option value="44">+44</option>
                <option value="61">+61</option>
                <option value="971">+971</option>
              </select>
              <input
                type="tel"
                id="reg-phone"
                className="rl__input"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter mobile number"
                autoComplete="tel-national"
                required
                maxLength={15}
              />
            </div>
          </div>
          <button type="submit" className="rl__submit" disabled={loading}>
            {loading ? <><Loader2 size={16} className="rl__spin" /> Sending OTP...</> : 'Send OTP'}
          </button>
        </form>
      )}

      {/* Phone OTP — code entry */}
      {regMode === 'phone' && otpStep === 'otp' && (
        <form className="rl__form" onSubmit={handleVerifyOtp}>
          <p className="rl__otp-note">
            OTP sent to +{countryCode} {phoneNumber}
          </p>
          {devOtp && (
            <p className="rl__otp-dev">
              Development OTP <strong>{devOtp}</strong>
            </p>
          )}
          <div className="rl__field">
            <label className="rl__label" htmlFor="reg-otp">Enter OTP</label>
            <input
              type="text"
              id="reg-otp"
              className="rl__input rl__otp-input"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="······"
              required
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
            />
          </div>
          <button type="submit" className="rl__submit" disabled={loading}>
            {loading ? <><Loader2 size={16} className="rl__spin" /> Verifying...</> : 'Verify OTP'}
          </button>
          <div className="rl__otp-actions">
            <button
              type="button"
              className="rl__link-btn"
              onClick={handleResendOtp}
              disabled={resendTimer > 0 || loading}
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
            </button>
            <button
              type="button"
              className="rl__link-btn"
              onClick={() => { setOtpStep('phone'); setOtpCode(''); setDevOtp(''); setError(''); }}
            >
              Change number
            </button>
          </div>
        </form>
      )}

      {/* Phone OTP — name + terms */}
      {regMode === 'phone' && otpStep === 'name' && (
        <form className="rl__form" onSubmit={handlePhoneRegister}>
          <p className="rl__otp-note">
            Mobile number verified. Enter your name to create your account.
          </p>
          <div className="rl__field">
            <label className="rl__label" htmlFor="reg-otp-name">Full Name</label>
            <input
              type="text"
              id="reg-otp-name"
              className="rl__input"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Your full name"
              autoComplete="name"
              required
            />
          </div>
          <div className="rl__terms">
            <input id="agree-phone" type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} />
            <label htmlFor="agree-phone">
              I agree to the{' '}<Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>{' '}and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
            </label>
          </div>
          <button type="submit" className="rl__submit" disabled={loading || !displayName.trim() || !agreedToTerms}>
            {loading ? <><Loader2 size={16} className="rl__spin" /> Creating account...</> : 'Create account & sign in'}
          </button>
        </form>
      )}
    </RoleLoginShell>
  );
}
